import type { FetchFunction } from '#layers/base/public';
import type { SchemaManifest } from '#layers/data-model/types/schema/schema-manifest';
import type { WorkspaceSchemaRows } from '#layers/data-model/utils/schema/decode-schema-manifest';
import type { WorkspaceEntityStore } from '#layers/runtime/public';
import { fetchAll } from '#layers/base/public';
import { decodeSchemaManifest } from '#layers/data-model/utils/schema/decode-schema-manifest';

/** The RStore collections populated from the manifest rather than /items. */
const SCHEMA_ROW_COLLECTIONS = [
	'MonospaceDataSource',
	'MonospaceNamespace',
	'MonospaceCollection',
	'MonospacePrimitiveField',
	'MonospaceSingleRelationField',
	'MonospaceSingleConstraint',
	'MonospaceSingleConstraintField',
	'MonospaceIndex',
	'MonospaceIndexField',
] as const;

// TODO expose the maximum allowed limit by the engine (set as query_limit_max in config)
const SCHEMA_PAGE_LIMIT = 1000;

interface PrepareWorkspaceSchemaOptions {
	store: WorkspaceEntityStore;
	workspaceFetch: FetchFunction;
}

export async function loadWorkspaceSchema({ store, workspaceFetch }: PrepareWorkspaceSchemaOptions): Promise<SchemaManifest> {
	// Metadata is not part of the engine's in-memory manifest. Fetch every flat
	// page into this runtime's cache, where the decoded schema proxies resolve it.
	// The engine adds no default ordering, so each paged read sorts on its key; without it, offset
	// pages may overlap or skip rows.
	const [schemaManifest, sources] = await Promise.all([
		workspaceFetch<SchemaManifest>('/schema'),
		fetchAll((params) => store.MonospaceDataSource.findMany({ fetchPolicy: 'fetch-only', sort: [{ id: { direction: 'asc' } }], params }), SCHEMA_PAGE_LIMIT),
		fetchAll((params) => store.MonospaceCollectionMeta.findMany({ fetchPolicy: 'fetch-only', sort: [{ collectionId: { direction: 'asc' } }], params }), SCHEMA_PAGE_LIMIT),
		fetchAll((params) => store.MonospacePrimitiveFieldMeta.findMany({ fetchPolicy: 'fetch-only', sort: [{ fieldId: { direction: 'asc' } }], params }), SCHEMA_PAGE_LIMIT),
		fetchAll((params) => store.MonospaceSingleRelationFieldMeta.findMany({ fetchPolicy: 'fetch-only', sort: [{ fieldId: { direction: 'asc' } }], params }), SCHEMA_PAGE_LIMIT),
	]);

	writeSchemaRows(decodeSchemaManifest(schemaManifest, sources), store);
	return schemaManifest;
}

function writeSchemaRows(rows: WorkspaceSchemaRows, store: WorkspaceEntityStore) {
	for (const name of SCHEMA_ROW_COLLECTIONS) {
		const collection = store.$collections.find((candidate) => candidate.name === name);
		if (collection) store.$cache.clearCollection({ collection });
	}

	for (const row of rows.dataSources) store.MonospaceDataSource.writeItem(row);
	for (const row of rows.namespaces) store.MonospaceNamespace.writeItem(row);
	for (const row of rows.collections) store.MonospaceCollection.writeItem(row);
	for (const row of rows.primitiveFields) store.MonospacePrimitiveField.writeItem(row);
	for (const row of rows.relationFields) store.MonospaceSingleRelationField.writeItem(row);
	for (const row of rows.constraints) store.MonospaceSingleConstraint.writeItem(row);
	for (const row of rows.constraintFields) store.MonospaceSingleConstraintField.writeItem(row);
	for (const row of rows.indexes) store.MonospaceIndex.writeItem(row);
	for (const row of rows.indexFields) store.MonospaceIndexField.writeItem(row);
}
